import {
  createStyles,
  List,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';

import { RouteContext, SidebarThemeContext } from 'src/context';
import { MobileCompanySwitcher } from 'src/features/companies/CompanySwitcher';
import { useCompanySwitcherVisibility } from 'src/features/companies/useCompanySwitcherVisibility';
import { Drawer } from 'src/features/drawer/Drawer';
import { SettingsSidebarBackToggle } from 'src/legacy/components/Sidebar/SettingsSidebarBackToggle';
import { SidebarLogo } from 'src/legacy/components/Sidebar/SidebarLogo';
import SidebarNav from 'src/legacy/components/Sidebar/SidebarNav';
import { useSidebarTheme } from 'src/legacy/components/Sidebar/useSidebarTheme';
import { RootState } from 'src/store';

const SidebarSkeletonItems = [
  { id: 'crm' },
  { id: 'messaging' },
  { id: 'files' },
];
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sidebarHeader: {
      padding: theme.spacing(2, 1, 3.5, 1),
      width: 'auto',
    },
    sidebarItemSkeleton: {
      borderRadius: theme.shape.borderRadius,
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      paddingBottom: theme.spacing(0.5),
      height: '100%',
    },
  }),
);

interface SidebarProps {
  openMobile: boolean;
  onMobileClose: () => void;
}

const SidebarContainer: React.FC<SidebarProps> = ({
  openMobile,
  onMobileClose,
}) => {
  const { sidebarTheme } = useSidebarTheme();
  const bannerOptions = useSelector(
    (state: RootState) => state.ui.bannerOptions,
  );
  const isSettingsLoaded = useSelector(
    (state: RootState) => state.settings.loaded,
  );
  const isExtensionsItemsLoaded = useSelector(
    (state: RootState) => state.dashboard.itemsLoaded,
  );
  const { visibility: companySwitcherVisibility } =
    useCompanySwitcherVisibility();

  const classes = useStyles({
    showBanner: Boolean(bannerOptions),
  });
  const { pathname } = useContext(RouteContext);
  const theme = useTheme();
  const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // show sidebar skeleton if app state is not ready
  // or settings are not loaded
  // or extensions items are not loaded yet
  const showSidebarSkeleton = !isSettingsLoaded || !isExtensionsItemsLoaded;
  const isSettingsPage = pathname.includes('settings');

  return (
    <SidebarThemeContext.Provider value={sidebarTheme}>
      <Drawer onClose={onMobileClose} open={openMobile}>
        <div className={classes.sidebarHeader}>
          {/* On main sidebar show portal logo */}
          {companySwitcherVisibility === 'mobile' ? (
            <MobileCompanySwitcher />
          ) : (
            <SidebarLogo isSettingsPage={isSettingsPage} />
          )}
          {/* On settings sidebar show back to portal button only on desktop */}
          {isSettingsPage && !isSmScreen && <SettingsSidebarBackToggle />}
        </div>
        <List className={classes.list}>
          {showSidebarSkeleton ? (
            SidebarSkeletonItems.map(({ id }) => (
              <div key={id} className="py-1 px-2">
                <Skeleton variant="rect" width="100%" height={28} />
              </div>
            ))
          ) : (
            <SidebarNav />
          )}
        </List>
      </Drawer>
    </SidebarThemeContext.Provider>
  );
};

export default SidebarContainer;
